import type {
  Cadence,
  ReportCustomerDto,
  ReportSectionDto,
} from '@act/shared/data-transfer-objects';
import { sectionSupportsInsightCards } from '@act/shared/data-transfer-objects';
import { FlexContainer } from '@act/storybook';
import { Typography } from '@mui/material';
import { getSectionMeta } from '../utils/reportDisplay.util';
import ReportDataRequirementTags from './ReportDataRequirementTags';
import ReportSectionPreview from './ReportSectionPreview';
import ReportSectionSettingToggle from './ReportSectionSettingToggle';
import ReportsSurface from './ReportsSurface';

export interface ReportBuilderSectionEditorProps {
  section: ReportSectionDto | undefined;
  customer: ReportCustomerDto | null | undefined;
  reportingPeriod: string;
  measurementPeriod: string;
  reportId: string;
  cadence: Cadence;
  savingSection: boolean;
  onIncludedChange: (section: ReportSectionDto, included: boolean) => void;
  onCalloutEnabledChange: (
    section: ReportSectionDto,
    calloutEnabled: boolean,
  ) => void;
  onClinicalInsightEnabledChange: (
    section: ReportSectionDto,
    enabled: boolean,
  ) => void;
  onFinancialInsightEnabledChange: (
    section: ReportSectionDto,
    enabled: boolean,
  ) => void;
  onFieldBlur: (
    section: ReportSectionDto,
    field: 'narrative' | 'callout' | 'calloutTitle',
    value: string,
  ) => void;
  onInsightFieldChange: (
    section: ReportSectionDto,
    dataSnapshot: Record<string, unknown> | null,
    card: 'clinical' | 'financial',
    field: 'heading' | 'body',
    value: string,
  ) => void;
  onInsightFieldBlur: (
    section: ReportSectionDto,
    dataSnapshot: Record<string, unknown> | null,
    card: 'clinical' | 'financial',
    field: 'heading' | 'body',
    value: string,
  ) => void;
}

/**
 * Main editor pane for the selected report section: settings, requirements, and preview.
 */
const ReportBuilderSectionEditor = ({
  section,
  customer,
  reportingPeriod,
  measurementPeriod,
  reportId,
  cadence,
  savingSection,
  onIncludedChange,
  onCalloutEnabledChange,
  onClinicalInsightEnabledChange,
  onFinancialInsightEnabledChange,
  onFieldBlur,
  onInsightFieldChange,
  onInsightFieldBlur,
}: ReportBuilderSectionEditorProps) => {
  const meta = section ? getSectionMeta(section.sectionId) : undefined;
  const insightCards =
    section !== undefined && sectionSupportsInsightCards(section.sectionId);

  if (!section || !meta) {
    return (
      <ReportsSurface padding={3}>
        <Typography color="text.secondary">
          Select a section to configure inclusion, insights, and preview content.
        </Typography>
      </ReportsSurface>
    );
  }

  return (
    <FlexContainer
      direction="column"
      gap={2}
      flexGrow={1}
      minWidth="0"
      width="100%"
      height="auto"
      overflowAllVisible={true}
    >
      <ReportsSurface padding={3}>
        <FlexContainer
          direction="column"
          gap={2}
          width="100%"
          height="auto"
          overflowAllVisible={true}
        >
          <FlexContainer
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={2}
            flexWrap={true}
            width="100%"
            height="auto"
            overflowAllVisible={true}
          >
            <FlexContainer
              direction="column"
              gap={0.5}
              flexGrow={1}
              height="auto"
              overflowAllVisible={true}
            >
              <Typography variant="h5" component="h2">
                {meta.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {meta.rationale}
              </Typography>
            </FlexContainer>
            <FlexContainer
              direction="row"
              gap={2}
              flexWrap={true}
              alignItems="center"
              height="auto"
              overflowAllVisible={true}
              flexShrink={0}
            >
              <ReportSectionSettingToggle
                label="Include"
                value={section.included}
                disabled={savingSection}
                onChange={included => onIncludedChange(section, included)}
              />
              {meta.supportsCallout ? (
                <ReportSectionSettingToggle
                  label="Callout"
                  value={section.calloutEnabled}
                  disabled={savingSection || !section.included}
                  onChange={calloutEnabled =>
                    onCalloutEnabledChange(section, calloutEnabled)
                  }
                />
              ) : null}
              {insightCards ? (
                <>
                  <ReportSectionSettingToggle
                    label="Insight #1"
                    value={section.clinicalInsightEnabled}
                    disabled={savingSection || !section.included}
                    onChange={enabled =>
                      onClinicalInsightEnabledChange(section, enabled)
                    }
                  />
                  <ReportSectionSettingToggle
                    label="Insight #2"
                    value={section.financialInsightEnabled}
                    disabled={savingSection || !section.included}
                    onChange={enabled =>
                      onFinancialInsightEnabledChange(section, enabled)
                    }
                  />
                </>
              ) : null}
            </FlexContainer>
          </FlexContainer>

          <ReportDataRequirementTags requirements={meta.dataRequirements} />

          <Typography variant="caption" color="text.secondary">
            Edit callout and insight copy directly in the preview below.
          </Typography>
        </FlexContainer>
      </ReportsSurface>

      <FlexContainer
        direction="column"
        gap={1}
        width="100%"
        height="auto"
        overflowAllVisible={true}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          style={{ letterSpacing: '0.08em' }}
        >
          Preview
        </Typography>
        <ReportSectionPreview
          section={{ ...section, source: 'draft' }}
          customer={customer}
          reportingPeriod={reportingPeriod}
          measurementPeriod={measurementPeriod}
          reportId={reportId}
          cadence={cadence}
          authoringEditable={true}
          onCalloutBlur={value => onFieldBlur(section, 'callout', value)}
          onCalloutTitleBlur={value =>
            onFieldBlur(section, 'calloutTitle', value)
          }
          onInsightFieldChange={(dataSnapshot, card, field, value) =>
            onInsightFieldChange(section, dataSnapshot, card, field, value)
          }
          onInsightFieldBlur={(dataSnapshot, card, field, value) =>
            onInsightFieldBlur(section, dataSnapshot, card, field, value)
          }
        />
      </FlexContainer>
    </FlexContainer>
  );
};

export default ReportBuilderSectionEditor;
